/**
 * A diet plan brought in from outside the AI: imported (a nutritionist's photo, PDF or text, read by the API's
 * POST /api/diet-plans/import) or built by hand. Both are edited as a draft, meal by meal, and saved as the same
 * 7-day weekPlan the AI writes, so the plan view, the calorie tracker and "Comi o planejado" work unchanged.
 *
 * A meal with options ("opção 1 / opção 2") keeps all of them in `options`; its `foods` is the option of that day
 * (`optionIndex`), which is what every existing screen reads.
 */
import { oneDecimal } from './portions';
import { planFoodFromSearch } from './foodEdit';
import { dayTotalsOf } from './freeMeal';

// The API's limits (src/api/diet-plan/utils/import-limits.js there). Photos are made small here before sending
export const IMPORT_LIMITS = { imageMaxBytes: 4 * 1024 * 1024, pdfMaxBytes: 10 * 1024 * 1024, pdfMaxPages: 30, textMaxChars: 20000 };
export const IMAGE_MAX_SIDE = 1600;
export const IMAGE_QUALITY = 0.82;
export const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

// The meals people pick from when building by hand, with the usual times
export const MEAL_PRESETS = [
  { key: 'breakfast', time: '07:00' },
  { key: 'morningSnack', time: '10:00' },
  { key: 'lunch', time: '12:30' },
  { key: 'afternoonSnack', time: '16:00' },
  { key: 'dinner', time: '19:30' },
  { key: 'supper', time: '21:30' },
];

let lastId = 0;
export const newId = () => {
  lastId += 1;
  return `m${lastId}`;
};

// ── Files ──────────────────────────────────────────────────────────────

/** The size of an image of `width` × `height` with its long side at most `maxSide`. */
export const fitWithin = (width, height, maxSide = IMAGE_MAX_SIDE) => {
  const longest = Math.max(width, height);
  if (!longest || longest <= maxSide) return { width, height };
  const ratio = maxSide / longest;
  return { width: Math.round(width * ratio), height: Math.round(height * ratio) };
};

/**
 * A photo made small enough to send (about 1600 px on the long side, JPEG at 0.82): a phone's 4 MB photo comes out
 * around 300 to 600 KB and still reads. Falls back to the original file when the browser can't decode it.
 */
export const downscaleImage = async (file, { maxSide = IMAGE_MAX_SIDE, quality = IMAGE_QUALITY } = {}) => {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const { width, height } = fitWithin(bitmap.width, bitmap.height, maxSide);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    // JPEG has no transparency: a PNG's clear parts become white, like the paper
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    // Let the canvas' pixels go now rather than whenever it's collected
    canvas.width = 0;
    canvas.height = 0;
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
};

/** A PDF's page count, read from its bytes (each page object says /Type /Page), or null when it can't be read. */
export const countPdfPages = async (file) => {
  try {
    const text = new TextDecoder('latin1').decode(await file.arrayBuffer());
    const pages = text.match(/\/Type\s*\/Page(?![a-zA-Z])/g);
    return pages ? pages.length : null;
  } catch {
    return null;
  }
};

/** What's wrong with a file before it's sent, or null: its type, its size, its pages. */
export const fileProblem = (file, pages = null) => {
  if (!file) return null;
  if (!ACCEPTED_TYPES.includes(file.type)) return { code: 'UNSUPPORTED_TYPE' };
  const isPdf = file.type === 'application/pdf';
  const maxBytes = isPdf ? IMPORT_LIMITS.pdfMaxBytes : IMPORT_LIMITS.imageMaxBytes;
  if (file.size > maxBytes) return { code: 'FILE_TOO_LARGE', sizeMb: file.size / 1024 / 1024, limitMb: maxBytes / 1024 / 1024 };
  if (isPdf && pages && pages > IMPORT_LIMITS.pdfMaxPages) return { code: 'TOO_MANY_PAGES', pages, limit: IMPORT_LIMITS.pdfMaxPages };
  return null;
};

// ── The import route ──────────────────────────────────────────────────

export class ImportError extends Error {
  constructor(code, details = {}) {
    super(code);
    this.name = 'ImportError';
    this.code = code;
    this.details = details;
  }
}

const apiOrigin = () => String(import.meta.env.VITE_GRAPHQL_URL || '').replace(/\/graphql$/, '');
export const importUrl = () => `${apiOrigin()}/api/diet-plans/import`;

/**
 * Sends the plan to be read and returns what was read (not saved). A file goes as multipart; pasted text too, so the
 * route takes one shape. nginx answers 413 in HTML before the API sees a file that's too big.
 */
export const readPlan = async ({ file, text, lang, signal, token = localStorage.getItem('authToken') }) => {
  const body = new FormData();
  if (file) body.append('file', file, file.name);
  else body.append('text', text);
  body.append('lang', lang === 'pt' ? 'pt' : 'en');
  body.append('healthDataConsent', 'true');

  let response;
  try {
    response = await fetch(importUrl(), { method: 'POST', body, signal, headers: token ? { Authorization: `Bearer ${token}` } : {} });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new ImportError('NETWORK');
  }
  if (response.status === 413) throw new ImportError('FILE_TOO_LARGE');
  const json = await response.json().catch(() => null);
  if (json?.error) throw new ImportError(json.error.details?.code || 'READ_FAILED', json.error.details || {});
  if (!response.ok || !json?.data) throw new ImportError('READ_FAILED');
  return json.data;
};

// ── The draft ─────────────────────────────────────────────────────────

/** A meal being built: its name, time, the plan's words for it, and its options (one, or "opção 1 / opção 2"). */
export const emptyMeal = (name = '', time = null) => ({
  id: newId(),
  name,
  time,
  sourceText: null,
  region: null,
  readable: true,
  options: [{ foods: [] }],
  active: 0,
  confirmed: false,
});

const toDraftMeal = (meal) => ({
  ...emptyMeal(meal.name || '', meal.time || null),
  sourceText: meal.sourceText || null,
  region: meal.region || null,
  readable: meal.readable !== false,
  options: meal.options?.length ? meal.options.map((option) => ({ foods: option.foods || [] })) : [{ foods: [] }],
});

/** What the import route read, as a draft to review: one menu per group of days (all seven when it's the same). */
export const draftFromImport = (data) => ({
  sameEveryDay: data.sameEveryDay !== false,
  groups: (data.days?.length ? data.days : [{ dayIndexes: [0, 1, 2, 3, 4, 5, 6], meals: [] }]).map((group) => ({
    dayIndexes: group.dayIndexes,
    meals: (group.meals || []).map(toDraftMeal),
  })),
  uncoveredDays: data.uncoveredDays || [],
  missingMeals: data.missingMeals || [],
  substitutions: data.substitutions || [],
  notes: data.notes || [],
});

/** A plan built by hand: one menu for the whole week, with the meals picked. */
export const manualDraft = (meals = []) => ({
  sameEveryDay: true,
  groups: [{ dayIndexes: [0, 1, 2, 3, 4, 5, 6], meals }],
  uncoveredDays: [],
  missingMeals: [],
  substitutions: [],
  notes: [],
});

/** The foods of the meal's open option. */
export const activeFoods = (meal) => meal.options[meal.active]?.foods || [];

const ZERO = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 };
export const foodsTotals = (foods) =>
  (foods || []).reduce(
    (sum, food) => ({
      calories: sum.calories + (food.calories || 0),
      protein: sum.protein + (food.protein || 0),
      carbs: sum.carbs + (food.carbs || 0),
      fat: sum.fat + (food.fat || 0),
      fiber: sum.fiber + (food.fiber || 0),
    }),
    ZERO
  );

/** A menu's day with each meal's open option (the review's numbers). */
export const menuTotals = (meals) => foodsTotals((meals || []).flatMap(activeFoods));

/** Whether any meal of the draft has more than one option (then the one question about them is asked). */
export const hasOptions = (draft) => draft.groups.some((group) => group.meals.some((meal) => meal.options.length > 1));

/** The draft's meals, every group's, in order. */
export const allMeals = (draft) => draft.groups.flatMap((group) => group.meals);

/** The meal with its open option's foods replaced. */
export const withActiveFoods = (meal, foods) => ({
  ...meal,
  options: meal.options.map((option, index) => (index === meal.active ? { ...option, foods } : option)),
});

/** A database food (values per 100 g) as a plan food of `grams`, keeping where it came from. */
export const planFoodFromResult = (result, grams, name) => ({
  ...planFoodFromSearch(result, grams, name),
  fiber: oneDecimal(((result.fiber || 0) * grams) / 100),
  grams,
  unit: 'g',
  source: result.source === 'open_food_facts' ? 'open_food_facts' : 'taco',
  foodId: result.id ?? null,
});

/** The meal's "não lido" state: the photo has it, but nothing in it could be read. */
export const isUnread = (meal) => !meal.readable && activeFoods(meal).length === 0;

// ── Saving ────────────────────────────────────────────────────────────

const planMeal = (meal, optionIndex) => {
  const index = Math.min(optionIndex, meal.options.length - 1);
  const base = { name: meal.name.trim(), foods: meal.options[index].foods };
  if (meal.time) base.time = meal.time;
  if (meal.options.length > 1) {
    base.options = meal.options.map((option) => ({ foods: option.foods }));
    base.optionIndex = index;
  }
  return base;
};

/**
 * The draft as the plan's seven days. Days a weekly plan doesn't cover take its first menu. With `alternate`, a meal
 * with options uses option 1 on Monday, 2 on Tuesday… (the shortcut for "alternar nos dias"); otherwise option 1
 * every day, changed on the day.
 */
export const weekFromDraft = (draft, { alternate = false, dayNames = [] } = {}) =>
  [0, 1, 2, 3, 4, 5, 6].map((dayIndex) => {
    const group = draft.groups.find((candidate) => candidate.dayIndexes.includes(dayIndex)) || draft.groups[0];
    const meals = group.meals
      .filter((meal) => meal.name.trim() && meal.options.some((option) => option.foods.length > 0))
      .map((meal) => ({ ...meal, options: meal.options.filter((option) => option.foods.length > 0) }))
      .map((meal) => planMeal(meal, alternate && meal.options.length > 1 ? dayIndex % meal.options.length : 0));
    return { dayIndex, dayName: dayNames[dayIndex] || String(dayIndex), meals };
  });

/** The plan's own numbers: its average day is the tracker's daily target (an imported plan has no questionnaire). */
export const planNutrition = (weekPlan) => {
  const days = (weekPlan || []).filter((day) => day.meals?.length);
  if (!days.length) return { targetCalories: 0, macros: { protein: 0, carbs: 0, fat: 0 }, fiber: 0, source: 'plan' };
  const sum = days.reduce((acc, day) => {
    const totals = dayTotalsOf(day.meals);
    const fiber = day.meals.flatMap((meal) => meal.foods || []).reduce((total, food) => total + (food.fiber || 0), 0);
    return { calories: acc.calories + totals.calories, protein: acc.protein + totals.protein, carbs: acc.carbs + totals.carbs, fat: acc.fat + totals.fat, fiber: acc.fiber + fiber };
  }, ZERO);
  const average = (value) => Math.round(value / days.length);
  return {
    targetCalories: average(sum.calories),
    macros: { protein: average(sum.protein), carbs: average(sum.carbs), fat: average(sum.fat) },
    fiber: average(sum.fiber),
    source: 'plan',
  };
};

/** What can't be saved yet: no meal with foods, a meal without a name, or a meal left empty (its id, to open it). */
export const draftProblem = (draft) => {
  const meals = allMeals(draft);
  const nameless = meals.find((meal) => !meal.name.trim() && meal.options.some((option) => option.foods.length));
  if (nameless) return { code: 'MEAL_NAME', mealId: nameless.id };
  const empty = meals.find((meal) => meal.options.every((option) => option.foods.length === 0));
  if (empty) return { code: 'MEAL_EMPTY', mealId: empty.id, name: empty.name };
  if (!meals.length) return { code: 'NO_MEALS' };
  return null;
};

/**
 * The createDietPlan / updateDietPlan input for an imported or hand-built plan. An imported plan keeps a copy of the
 * week as the nutritionist wrote it ("Voltar ao plano do nutricionista"); the photo is never part of it.
 */
export const planPayload = (draft, { origin, alternate = false, dayNames = [] }) => {
  const weekPlan = weekFromDraft(draft, { alternate, dayNames });
  return {
    origin,
    weekPlan,
    originalWeekPlan: origin === 'imported' ? weekPlan : null,
    substitutions: draft.substitutions?.length ? draft.substitutions : null,
    notes: draft.notes?.length ? draft.notes : null,
    nutritionData: planNutrition(weekPlan),
    healthDataConsent: true,
  };
};

// ── The plan view ─────────────────────────────────────────────────────

/** The meal on its option `index` for the day: `foods` follows, so every screen shows the one chosen. */
export const chooseOption = (meal, index) =>
  meal.options?.[index] ? { ...meal, foods: meal.options[index].foods, optionIndex: index } : meal;

/** A meal whose foods were changed by hand keeps its option in step, so switching away and back keeps the change. */
export const syncActiveOption = (meal) => {
  if (!meal.options?.length) return meal;
  const index = meal.optionIndex ?? 0;
  return { ...meal, options: meal.options.map((option, at) => (at === index ? { ...option, foods: meal.foods } : option)) };
};

const plain = (text) =>
  String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
const STOP = new Set(['de', 'da', 'do', 'das', 'dos', 'com', 'sem', 'e', 'ou', 'em', 'a', 'o']);
const keyWords = (text) =>
  plain(text)
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 2 && !STOP.has(word));
// The same food when either name's first word is in the other ("Peito de frango grelhado" and "Frango"), so
// "Pão integral" and "Arroz integral" aren't
const sameFood = (a, b) => {
  const wordsA = keyWords(a);
  const wordsB = keyWords(b);
  if (!wordsA.length || !wordsB.length) return false;
  return wordsB.includes(wordsA[0]) || wordsA.includes(wordsB[0]);
};

/**
 * The plan's substitutions for a food, in the nutritionist's amounts: the group written for it ("Arroz integral = …"),
 * or a group that lists it among the equivalents (then the other equivalents).
 */
export const substitutesFor = (substitutions, food) => {
  if (!food || !substitutions?.length) return [];
  const items = [];
  substitutions.forEach((group) => {
    const groupItems = group.items || [];
    if (sameFood(food.name, group.forName)) {
      items.push(...groupItems);
      return;
    }
    const at = groupItems.findIndex((item) => sameFood(food.name, item.name));
    if (at === -1) return;
    items.push(...groupItems.filter((_, index) => index !== at));
  });
  return items.filter((item) => item && !sameFood(item.name, food.name) && item.calories != null);
};

/** Whether the plan comes from outside the AI: imported or built by hand. Old plans have no origin and are the AI's. */
export const isOwnPlan = (origin) => origin === 'imported' || origin === 'manual';
